import React, { useState, useEffect, useCallback } from 'react'
import { Users, Plus, Shield, Loader2, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import ResetPasswordButton from '../../../components/admin/ResetPasswordButton'

const API_URL = process.env.REACT_APP_BACKEND_URL

export default function AdminCollaborateurs() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/c2`)
      if (res.ok) {
        const data = await res.json()
        setUsers(data.users || [])
      }
    } catch (err) {
      toast.error('Erreur chargement')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="collab-page-title">Collaborateurs C2</h1>
          <p className="text-sm text-slate-500 mt-1">Gestionnaires et administrateurs de la plateforme</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
          data-testid="btn-create-collab"
        >
          <Plus className="w-4 h-4" />
          Nouveau collaborateur
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Aucun collaborateur C2</p>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid={`collab-card-${u.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{u.prenom} {u.nom}</p>
                  <p className="text-sm text-slate-500">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  u.role === 'master_admin' ? 'bg-amber-100 text-amber-700' :
                  u.role === 'c2_admin' ? 'bg-indigo-100 text-indigo-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {u.role === 'master_admin' ? 'Master Admin' :
                   u.role === 'c2_admin' ? 'Admin C2' :
                   u.role === 'c2_gestionnaire' ? 'Gestionnaire' : u.role}
                </span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  u.statut === 'actif' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                }`}>
                  {u.statut || 'actif'}
                </span>
                <ResetPasswordButton user={u} />
                {u.role !== 'master_admin' && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation()
                      if (!window.confirm(`Supprimer ${u.prenom} ${u.nom} ?`)) return
                      try {
                        const res = await fetch(`${API_URL}/api/admin/users/${u.id}`, { method: 'DELETE' })
                        if (res.ok) { toast.success('Collaborateur supprimé'); fetchUsers() }
                        else toast.error('Erreur suppression')
                      } catch { toast.error('Erreur') }
                    }}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title="Supprimer"
                    data-testid={`delete-collab-${u.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCollabModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchUsers() }}
        />
      )}
    </div>
  )
}

function CreateCollabModal({ onClose, onCreated }) {
  const [formData, setFormData] = useState({
    email: '', password: '', prenom: '', nom: '', role: 'c2_gestionnaire'
  })
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.email || !formData.password || !formData.nom) return
    setSaving(true)

    try {
      const res = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      if (res.ok) {
        toast.success('Collaborateur créé')
        onCreated()
      } else {
        const err = await res.json()
        toast.error(err.detail || 'Erreur lors de la création')
      }
    } catch (err) {
      toast.error('Erreur réseau')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Nouveau collaborateur C2</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prénom *</label>
              <input
                type="text" required
                value={formData.prenom}
                onChange={e => setFormData({...formData, prenom: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="collab-prenom"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
              <input
                type="text" required
                value={formData.nom}
                onChange={e => setFormData({...formData, nom: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="collab-nom"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <input
              type="email" required
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              placeholder="prenom.nom@c2-assurances.fr"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="collab-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe *</label>
            <input
              type="password" required minLength={8}
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              placeholder="Minimum 8 caractères"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="collab-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Rôle *</label>
            <select
              value={formData.role}
              onChange={e => setFormData({...formData, role: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="collab-role"
            >
              <option value="c2_gestionnaire">Gestionnaire C2</option>
              <option value="c2_admin">Admin C2</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || !formData.email || !formData.password || !formData.nom}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              data-testid="collab-submit"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
